/**
 * Every LaunchDarkly flag the platform reads, typed so a typo is a compile error rather than a
 * silently-false flag.
 *
 * This file replaced four placeholder keys — `dark-mode`, `new-dashboard`, `beta-features`,
 * `advanced-search` — that existed only to give the not-yet-wired hooks something to return. None of them
 * were ever created in LaunchDarkly.
 *
 * Naming follows `docs/runbooks/feature-flags.md`: `{domain}-{feature}`, kebab-case.
 */
export type FeatureFlags = {
    /**
     * Whether the age-restriction feature is active at all.
     *
     * Read by the frontend **and** the backend from this one key, so the two can never disagree
     * about whether ages are in play. It gates three separate things: whether a date of birth may be
     * collected, whether it may be read back, and whether `content.minimum_age` is enforced.
     *
     * Exists because of a FERPA concern about holding learner ages. `users.date_of_birth` and
     * `content.minimum_age` stay in the model either way — with the flag off nothing collects an age
     * and nothing is checked, and rated content stays visible to everyone rather than hidden behind
     * a gate nobody can pass.
     */
    birthdateEnabled: boolean;
    inAppChatEnabled: boolean;
    lmsEnabled: boolean;
};

export type FeatureFlagKey = keyof FeatureFlags;

/**
 * What a flag means when LaunchDarkly has not answered — no client, no network, or a evaluation that
 * timed out.
 *
 * **`birthdateEnabled` fails closed on purpose**, unlike an ordinary feature flag. For a gate that
 * exists to satisfy a privacy concern, the safe direction when we cannot reach LaunchDarkly is "do
 * not collect and do not enforce". A flag that failed open would start collecting dates of birth
 * during an outage, which is the one outcome the flag was added to prevent.
 */
export const FEATURE_FLAG_DEFAULTS: FeatureFlags = {
    birthdateEnabled: false,
    inAppChatEnabled: false,
    lmsEnabled: false,
};

/** Kept as the older name so existing imports keep resolving. Prefer `FeatureFlagKey`. */
export type FeatureFlag = FeatureFlagKey;
